import { resolveBackTarget } from '@/utils/helpers'
import { DETAIL_WITH_COMMENTS_MAX_WIDTH, STAGE_BACKDROP_COLOR } from '@/utils/constants'

export default [
  {
    path: '/collections',
    components: {
      default: () => import('@/views/Collections/Collections.vue'),
      appbar: () => import('@/views/Bookmarks/components/BookmarkAppBar.vue'),
    },
    meta: {
      appbarVisibility: true,
      requiresAuth: true,
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Pin collection
  {
    path: '/collections/:collectionId/pin',
    component: () => import('@/views/CollectionItems/EditPinPosition.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Create
  {
    path: '/collections/add',
    component: () => import('@/views/Collections/CreateCollection.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        backButton: {
          // Flows that carry a back target (the add-details collection picker)
          // must return to it, not to the previous history entry — which is the
          // step before the picker. Without ?from= keep plain history back.
          to: route => resolveBackTarget(route, null),
        },
      },
    },
  },
  // View bookmarks, order matters
  {
    path: '/collections/:id',
    components: {
      default: () => import('@/views/Collections/Collection.vue'),
      sidebar: () => import('@/views/Collections/components/CollectionSidebar.vue'),
      appbar: () => import('@/views/Collections/components/CollectionAppBar.vue'),
    },
    meta: {
      sidebarVisibility() {
        return true
      },
      appbarVisibility: true,
      requiresAuth: true,
      transition: 't-fade',
      layoutTransition: 't-expand',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Edit
  {
    path: '/collections/:id/edit',
    component: () => import('@/views/Collections/EditCollection.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          // invite pages redirect to this, we don't want to use regular back button
          to: from => `/collections/${from.params.id}`,
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Tier list Layout settings. No role guard: the entry points are gated by
  // canWriteCanonical and the api answers INSUFFICIENT_PRIVILEGE (ADR-0005).
  {
    path: '/collections/:id/layouts/tierlist',
    component: () => import('@/views/Collections/EditTierListLayout.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          to: from => `/collections/${from.params.id}`,
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Presentation mode (ADR-0006): the launch page and the stage, two routes of the
  // app in the same tab. Both need a session and no role guard — every member may
  // present, the api stays the guard for writes. A collection whose tier list is
  // off is sent back to its page by the pages themselves.
  {
    path: '/collections/:id/presentation',
    component: () => import('@/views/Collections/presentation/PresentationLaunch.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          to: from => `/collections/${from.params.id}`,
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/presentation/stage',
    component: () => import('@/views/Collections/presentation/PresentationStage.vue'),
    meta: {
      requiresAuth: true,
      layout: 'fullscreen',
      transition: 't-fade',
      layoutTransition: 't-fade',
      // The bars around the stage reach into the safe-area strip.
      systemBar: {
        color: STAGE_BACKDROP_COLOR,
      },
    },
    // An item's details, opened by a click on its card, take the board's place:
    // child routes, so the stage (its board, its fullscreen) stays mounted and
    // the browser's back button closes them. The member pages themselves, limited
    // to the like, the comments and the clickout (presentationItemPath).
    children: [
      {
        path: 'bookmarks/:bookmarkId',
        component: () => import('@/views/Bookmarks/BookmarkDetail.vue'),
        props: { presentation: true },
      },
      {
        path: 'notes/:noteId',
        component: () => import('@/views/Notes/NoteDetail.vue'),
        props: { presentation: true },
      },
    ],
  },
  // Pick items to place in the collection description (intro items)
  {
    path: '/collections/:id/intro-items/pick',
    component: () => import('@/views/Collections/PickIntroItems.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          // Opened from the settings page with ?from= (pushWithBack); the
          // fallback covers a direct deep-link.
          to: route => resolveBackTarget(route, `/collections/${route.params.id}/edit`),
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Analytics (public-page stats; owner/admin gated in component + backend)
  {
    path: '/collections/:id/analytics',
    component: () => import('@/views/Collections/CollectionAnalytics.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus-detail',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          // `from` set when opened from a parent collection's analytics page; the
          // fallback covers direct access (⋯ menu on the collection itself)
          to: route => resolveBackTarget(route, `/collections/${route.params.id}`),
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Per-item analytics (owner/admin gated in component + backend)
  {
    path: '/collections/:id/analytics/items/:itemId',
    component: () => import('@/views/Collections/CollectionItemAnalytics.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus-detail',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(route, `/collections/${route.params.id}/analytics`),
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/access',
    component: () => import('@/views/Collections/SubCollectionUserAccess.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(route, `/collections/${route.params.id}`),
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    // Same view as /collections/add — the route only preselects the parent and
    // keeps its own back-button behaviour.
    path: '/collections/:id/add-sub-collection',
    component: () => import('@/views/Collections/CreateCollection.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          // invite pages redirect to this, we don't want to use regular back button
          to: from => resolveBackTarget(from, `/collections/${from.params.id}`),
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/members',
    component: () => import('@/views/Collections/ManageMembers.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(route, `/collections/${route.params.id}`),
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/members/:memberId/edit',
    component: () => import('@/views/Collections/EditMemberRole.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(route, `/collections/${route.params.id}/members`),
        },
      },
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/bulk/edit-share-link',
    component: () => import('@/views/Collections/BulkEditShareLink.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/edit-share-link',
    component: () => import('@/views/Collections/EditShareLink.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/bulk/invite',
    component: () => import('@/views/Collections/BulkInviteMembers.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/invite',
    component: () => import('@/views/Collections/InviteMembers.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Join
  {
    path: '/collections/:id/invite-link/:token',
    component: () => import('@/views/Collections/ShareLinkJoin.vue'),
    meta: {
      requiresAuth: true,
      layout: 'fullscreen',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/invite-email/:token',
    component: () => import('@/views/Collections/InviteAccept.vue'),
    meta: {
      requiresAuth: true,
      layout: 'fullscreen',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // Pin item in collection
  {
    path: '/collections/:id/items/:itemId/pin',
    component: () => import('@/views/CollectionItems/EditPinPosition.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // bookmarks
  {
    path: '/collections/:id/bookmarks/:bookmarkId',
    component: () => import('@/views/Bookmarks/BookmarkDetail.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus-detail',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        maxWidthDesktop: DETAIL_WITH_COMMENTS_MAX_WIDTH,
        backButton: {
          // ?from= honored so detour flows (e.g. the intro-item picker) get
          // the user back; without it behaves as before.
          to: from => resolveBackTarget(from, `/collections/${from.params.id}`),
        },
      },
    },
  },
  {
    path: '/collections/:id/bookmarks/:bookmarkId/comments',
    redirect: to => `/collections/${to.params.id}/bookmarks/${to.params.bookmarkId}`,
  },
  {
    path: '/collections/:id/bulk/add-tags',
    component: () => import('@/views/Bookmarks/BulkEditTags.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/bulk/remove-tags',
    component: () => import('@/views/Bookmarks/BulkRemoveTags.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:id/bookmarks/:bookmarkId/likes',
    component: () => import('@/views/CollectionItems/ItemLikes.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(
            route,
            `/collections/${route.params.id}/bookmarks/${route.params.bookmarkId}`,
          ),
        },
      },
    },
  },
  {
    path: '/collections/:id/notes/:noteId/likes',
    component: () => import('@/views/CollectionItems/ItemLikes.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(
            route,
            `/collections/${route.params.id}/notes/${route.params.noteId}`,
          ),
        },
      },
    },
  },
  {
    path: '/collections/:id/bookmarks/:bookmarkId/tags',
    component: () => import('@/views/Bookmarks/EditTags.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(
            route,
            `/collections/${route.params.id}/bookmarks/${route.params.bookmarkId}`,
          ),
        },
      },
    },
  },
  {
    path: '/collections/:id/notes/:noteId/tags',
    component: () => import('@/views/Bookmarks/EditTags.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        backButton: {
          to: route => resolveBackTarget(
            route,
            `/collections/${route.params.id}/notes/${route.params.noteId}`,
          ),
        },
      },
    },
  },
  {
    path: '/collections/:id/bookmarks/:bookmarkId/edit',
    component: () => import('@/views/Bookmarks/Edit.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        backButton: {
          to: from => `/collections/${from.params.id}/bookmarks/${from.params.bookmarkId}`,
        },
      },
    },
  },
  // notes
  {
    path: '/collections/:id/notes/:noteId',
    component: () => import('@/views/Notes/NoteDetail.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus-detail',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
      layoutProps: {
        maxWidthDesktop: DETAIL_WITH_COMMENTS_MAX_WIDTH,
        backButton: {
          // ?from= honored so detour flows (e.g. the intro-item picker) get
          // the user back; without it behaves as before.
          to: from => resolveBackTarget(from, `/collections/${from.params.id}`),
        },
      },
    },
  },
  {
    path: '/collections/:id/notes/:noteId/edit',
    component: () => import('@/views/Notes/EditNote.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // INFO: param naming is like this cause it is shared with the bookmark route
  {
    path: '/collections/:collectionId/notes/:id/add-collections',
    component: () => import('@/views/CollectionItems/AddToCollections.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  // INFO: param naming is like this cause it is shared with the bookmark route
  {
    path: '/collections/:collectionId/bookmarks/:id/add-collections',
    component: () => import('@/views/CollectionItems/AddToCollections.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
  {
    path: '/collections/:collectionId/bulk/add-collections',
    component: () => import('@/views/Bookmarks/BulkAddToCollections.vue'),
    meta: {
      requiresAuth: true,
      layout: 'focus',
      transition: 't-fade',
      layoutTransition: 't-fade',
      systemBar: {
        color: 'neutral0',
      },
    },
  },
]
